// src/defaults.js
var defaultConfig = {
  // Core Messaging initialization parameters
  orgId: "00DWe000001HXF7",
  deploymentId: "Liquidweb_Support_Portal",
  communityURL: "https://liquidweb--developer.sandbox.my.site.com/ESWLiquidwebSupportPort1736532853150",
  scrt2URL: "https://liquidweb--developer.sandbox.my.salesforce-scrt.com",
  settings: {
    language: "en_US",
    chatButtonPosition: "32px,32px",
    enableUserInputForConversationWithBot: false
  },
  // Messaging Service for Web script location
  messagingScriptURL: "https://liquidweb--developer.sandbox.my.site.com/ESWLiquidwebSupportPort1736532853150/assets/js/bootstrap.min.js",
  // Registrable domain where store cookies
  storageDomain: "liquidweb.com",
  // When the chat button is clicked, the amount of time
  // to wait for the script to report "ready" before
  // assuming something went wrong. Not necessarily a hard
  // failure. The user could just have a slow connection.
  chatStartTimeout: 3e4,
  // Analytics configuration
  analyticsMode: "dataLayer",
  // The context for web analytics action names (context:object_action)
  analyticsActionContext: "lw",
  // Disable if Google Analytics is not implemented.
  useGoogleAnalytics: true,
  // Amount of time to wait for Google Analytics to return
  // a tracker object. The script may block for up to this
  // duration in some situations, such as when an ad blocker
  // has replaced window.ga with a no-op function.
  googleAnalyticsTrackerTimeout: 2500,
  // A cookie to track the last time startChat was called,
  // regardless of success. Used by UI implementations to
  // control things like deferred initialization.
  lastStartTimeCookieName: "lwChatLastStartTime"
};
var defaults_default = defaultConfig;

// src/config.js
var currentConfig = defaults_default;
var mergeConfig = (newConfig) => {
  currentConfig = {
    ...currentConfig,
    ...newConfig,
    settings: {
      ...currentConfig.settings,
      ...newConfig.settings
    }
  };
  return currentConfig;
};
var getAllConfig = () => currentConfig;
var getConfig = (key) => currentConfig[key];

// src/util/inject-script.js
var injectScript = (src) => new Promise((resolve, reject) => {
  const scriptEl = document.createElement("script");
  scriptEl.src = src;
  scriptEl.async = true;
  scriptEl.onload = () => resolve(scriptEl);
  scriptEl.onerror = () => reject(new Error(`Failed to load ${src}`));
  document.head.appendChild(scriptEl);
});
var inject_script_default = injectScript;

// src/emitter.js
import Emitter from "component-emitter";
var emitter = new Emitter();
var emitter_default = emitter;

// src/messaging-service.js
var loadPromise = null;
var loadMessagingService = () => {
  if (loadPromise) return loadPromise;
  if (window.embeddedservice_bootstrap) return Promise.resolve();
  const url = getConfig("messagingScriptURL");
  loadPromise = inject_script_default(url).catch((err) => {
    emitter_default.emit("LWMessagingScriptLoadFailure");
    throw err;
  });
  return loadPromise;
};
var getMessagingService = async () => {
  if (window.embeddedservice_bootstrap) return window.embeddedservice_bootstrap;
  await loadMessagingService();
  return window.embeddedservice_bootstrap;
};

// src/google-analytics.js
var getGoogleAnalyticsObjectName = () => {
  window.GoogleAnalyticsObject = window.GoogleAnalyticsObject || "ga";
  return window.GoogleAnalyticsObject;
};
var maybeStubGoogleAnalytics = () => {
  const gaName = getGoogleAnalyticsObjectName();
  if (window[gaName]) return;
  window[gaName] = window[gaName] || function() {
    window[gaName].q = window[gaName].q || [];
    window[gaName].q.push(arguments);
  };
  window[gaName].l = (/* @__PURE__ */ new Date()).getTime();
};
var getGoogleAnalyticsObject = () => {
  const useGoogleAnalytics = getConfig("useGoogleAnalytics");
  if (!useGoogleAnalytics) return void 0;
  maybeStubGoogleAnalytics();
  const gaName = getGoogleAnalyticsObjectName();
  return window[gaName];
};
var awaitGAReadyPromise;
var awaitGoogleAnalyticsReady = () => {
  if (awaitGAReadyPromise) return awaitGAReadyPromise;
  const ga = getGoogleAnalyticsObject();
  if (!ga) return Promise.resolve(void 0);
  const trackerTimeout = getConfig("googleAnalyticsTrackerTimeout");
  awaitGAReadyPromise = new Promise((resolve, reject) => {
    ga((tracker) => {
      setTimeout(() => resolve(tracker));
    });
    setTimeout(reject, trackerTimeout);
  }).catch(
    () => (
      // Fail gracefully in case of timeout
      Promise.resolve(void 0)
    )
  );
  return awaitGAReadyPromise;
};
var getTrackerPromise;
var getTracker = () => {
  if (getTrackerPromise) return getTrackerPromise;
  getTrackerPromise = awaitGoogleAnalyticsReady().then((tracker) => {
    if (tracker) return tracker;
    const ga = getGoogleAnalyticsObject();
    if (typeof ga.getAll === "function") {
      return ga.getAll()[0];
    }
    return void 0;
  });
  return getTrackerPromise;
};
var getClientId = () => getTracker().then((tracker) => tracker ? tracker.get("clientId") : void 0);
var getTrackingId = () => getTracker().then((tracker) => tracker ? tracker.get("trackingId") : void 0);
var sendEvent = ({ category, action, label }) => getTracker().then((tracker) => {
  if (!tracker) return;
  tracker.send("event", category, action, label);
});

// src/analytics.js
var DEFAULT_EVENT_CATEGORY = "chat";
var DATALAYER_EVENT_NAME = "lw.structured";
var DATALAYER_IDENTIFY_EVENT_NAME = "lw.userIdentify";
var DATALAYER_NAME = "dataLayer";
var analyticsEvents = {
  onAgentJoinedConference: {
    action: "agent_joined"
  },
  onAgentLeftConference: {
    action: "agent_left"
  },
  onAgentMessage: {
    action: "message_sent",
    label: "agent"
  },
  onChasitorMessage: {
    action: "message_sent",
    label: "chasitor"
  },
  onChatConferenceEnded: {
    action: "conference_ended"
  },
  onChatConferenceInitiated: {
    action: "conference_initiated"
  },
  onChatEndedByAgent: {
    action: "chat_ended",
    label: "agent"
  },
  onChatEndedByChasitor: {
    action: "chat_ended",
    label: "chasitor"
  },
  onChatReconnectSuccessful: {
    action: "chat_reconnected"
  },
  onChatRequestSuccess: {
    action: "chat_succeeded"
  },
  onChatTransferSuccessful: {
    action: "chat_transferred"
  },
  onConnectionError: {
    action: "error_occurred",
    label: "connection"
  },
  onIdleTimeoutOccurred: {
    action: "timeout_occurred",
    label: "idle"
  },
  onQueueUpdate: {
    action: "queue_updated"
  },
  onClickSubmitButton: {
    action: "submitButton_clicked"
  },
  onHelpButtonClick: {
    action: "helpButton_clicked"
  },
  onInviteAccepted: {
    action: "invite_accepted"
  },
  onInviteRejected: {
    action: "invite_rejected"
  },
  LWButtonClickTimeout: {
    action: "timeout_occurred",
    label: "click"
  },
  LWESWScriptLoadFailure: {
    action: "error_occurred",
    label: "scriptLoadFailure"
  },
  LWPrechatSurveySubmitted: {
    action: "prechatSurvey_submitted",
    getLabel: (data = []) => data.find((i) => i.label === "Department")?.value
  }
};
var sendDataLayerEvent = ({ category, action, label, value, isNonInteraction = false }) => window[DATALAYER_NAME].push({
  event: DATALAYER_EVENT_NAME,
  data: {
    category,
    action,
    label,
    value,
    isNonInteraction
  }
});
var sendEvent2 = ({ category = DEFAULT_EVENT_CATEGORY, action, label }) => {
  const { analyticsMode, analyticsActionContext } = getAllConfig();
  const actionWithContext = `${analyticsActionContext}:${action}`;
  switch (analyticsMode) {
    case "googleAnalytics":
      sendEvent({
        category,
        action: actionWithContext,
        label
      });
      break;
    case "dataLayer":
      sendDataLayerEvent({
        category,
        action: actionWithContext,
        label
      });
      break;
    default:
  }
};
var handlePrechatSurveyEvent = (eventName, data) => {
  if (eventName !== "LWPrechatSurveySubmitted") return;
  const details = {};
  data.forEach((detail) => {
    details[detail.label] = detail.value;
  });
  const analyticsMode = getConfig("analyticsMode");
  if (analyticsMode !== "dataLayer") return;
  if (!details.Email) return;
  window[DATALAYER_NAME].push({
    event: DATALAYER_IDENTIFY_EVENT_NAME,
    userIdentify: {
      email: details.Email,
      lastname: details.Lastname,
      firstname: details.Firstname,
      identify_source: "Chat"
    }
  });
};
var getShouldSkipAnalytics = (eventName, data) => {
  if (eventName === "LWPrechatSurveySubmitted") {
    return !!data.find((detail) => detail.label === "isInvitation")?.value;
  }
  return false;
};
var handleEvent = (eventName, data) => {
  const { action, label, getLabel } = analyticsEvents[eventName];
  if (!action) return;
  handlePrechatSurveyEvent(eventName, data);
  const shouldSkipAnalytics = getShouldSkipAnalytics(eventName, data);
  if (shouldSkipAnalytics) return;
  if (typeof getLabel === "function") {
    sendEvent2({
      action,
      label: getLabel(data)
    });
    return;
  }
  sendEvent2({ action, label });
};
var watchEvents = () => {
  Object.keys(analyticsEvents).forEach((eventName) => {
    emitter_default.on(eventName, handleEvent.bind(null, eventName));
  });
};
var maybeStubDatalayer = () => {
  const analyticsMode = getConfig("analyticsMode");
  if (analyticsMode === "dataLayer") {
    window[DATALAYER_NAME] = window[DATALAYER_NAME] || [];
  }
};
var init = () => {
  maybeStubDatalayer();
  watchEvents();
};

// src/util/cookie.js
var getCookie = (name) => decodeURIComponent(
  document.cookie.replace(new RegExp(`(?:(?:^|.*;)\\s*${name}\\s*\\=\\s*([^;]*).*$)|^.*$`), "$1")
) || null;
var setCookie = (name, value, options = {}) => {
  let expiresStr = "";
  if (options.expires) {
    const time = (/* @__PURE__ */ new Date()).getTime() + 864e5 * options.expires;
    expiresStr = `;expires=${new Date(time).toUTCString()}`;
  }
  const domainStr = options.domain ? `;domain=${options.domain}` : "";
  document.cookie = `${name}=${value};path=/${domainStr}${expiresStr}`;
};

// src/extra-details/affiliate.js
var getLWReferralData = () => {
  const cookieValue = getCookie("lwReferralData");
  if (!cookieValue) return void 0;
  const cookieData = JSON.parse(cookieValue);
  switch (cookieData.program) {
    case "impact_radius":
      return {
        Network: cookieData.program,
        PartnerId: cookieData.irpid,
        AdId: cookieData.iradid,
        SubId: cookieData.sharedid,
        ClickId: cookieData.clickid
      };
    case "awin":
      return {
        Network: cookieData.program,
        PartnerId: cookieData.awid,
        AdId: cookieData.aid,
        SubId: cookieData.sid,
        ClickId: cookieData.awc
      };
    default:
      return void 0;
  }
};
var getCJReferralData = () => {
  const pidCookie = getCookie("CJPIDLW");
  const eventCookie = getCookie("CJEVENTLW");
  if (!pidCookie) return void 0;
  const fields = pidCookie.match(/pid=([^&]+)&aid=([^&]+)?&sid=([^&]+)?/);
  if (!fields) return void 0;
  return {
    Network: "cj",
    PartnerId: fields[1],
    AdId: fields[2],
    SubId: fields[3],
    ClickId: eventCookie
  };
};
var getAffiliateData = () => getLWReferralData() || getCJReferralData();
var getInitialExtraDetails = () => {
  const affiliateData = getAffiliateData();
  if (!affiliateData) return [];
  return Object.entries(affiliateData).map(([key, value]) => ({
    label: `Affiliate${key}__c`,
    value,
    displayToAgent: true,
    transcriptFields: [`Affiliate${key}__c`]
  }));
};
var affiliate_default = getInitialExtraDetails;

// src/extra-details/auth-token.js
var getInitialExtraDetails2 = () => [
  {
    label: "ChatAuthToken",
    value: "",
    displayToAgent: false,
    transcriptFields: ["Chat_Auth_Token__c"]
  }
];
var auth_token_default = getInitialExtraDetails2;

// src/extra-details/coupon.js
var getInitialExtraDetails3 = () => {
  const cookieValue = getCookie("LW_COUPON");
  if (!cookieValue) return [];
  const fields = cookieValue.match(/([^|]+)|/);
  if (!fields) return [];
  const couponCode = fields[1];
  if (!couponCode) return [];
  return [
    {
      label: "SessionCouponCode__c",
      value: couponCode,
      displayToAgent: true,
      transcriptFields: ["SessionCouponCode__c"]
    }
  ];
};
var coupon_default = getInitialExtraDetails3;

// src/extra-details/google-analytics.js
var formatDetails = ([clientId, trackingId]) => {
  if (!clientId || !trackingId) {
    return [
      {
        label: "googleTrackingUnavailable",
        value: true,
        displayToAgent: false
      }
    ];
  }
  return [
    {
      label: "googleClientId",
      value: clientId,
      displayToAgent: true,
      transcriptFields: ["Google_Client_Id__c"]
    },
    {
      label: "googleTrackingId",
      value: trackingId,
      displayToAgent: true,
      transcriptFields: ["Google_Tracking_Id__c"]
    }
  ];
};
var getInitialExtraDetails4 = () => Promise.all([getClientId(), getTrackingId()]).then(formatDetails);
var google_analytics_default = getInitialExtraDetails4;

// src/extra-details/pii-prefs.js
var getInitialExtraDetails5 = () => {
  const piiCookie = getCookie("lwPiiPreferences");
  if (!piiCookie) return [];
  const piiDetails = JSON.parse(piiCookie);
  return Object.entries(piiDetails).map(([key, value]) => ({
    label: `piiPref_${key}`,
    value,
    displayToAgent: false
  }));
};
var pii_prefs_default = getInitialExtraDetails5;

// src/extra-details/preselect-department.js
var getInitialExtraDetails6 = () => {
  const preselectDepartment = getConfig("preselectDepartment");
  return [
    {
      label: "preselectDepartment",
      value: preselectDepartment || "",
      displayToAgent: false
    },
    {
      label: "isInvitation",
      value: false,
      displayToAgent: false
    }
  ];
};
var preselect_department_default = getInitialExtraDetails6;

// src/extra-details/tracking-cookies.js
var cookieToSalesforceFieldMap = {
  _utma: "Google_Utma__c",
  _utmc: "Google_Utmc__c",
  _utmz: "Google_Utmz__c",
  utm_source: "Google_Utm_Source__c",
  utm_medium: "Google_Utm_Medium__c",
  utm_channel: "Google_Utm_Channel__c",
  utm_campaign: "Google_Utm_Campaign__c",
  gclid: "Google_Gclid__c"
};
var getInitialExtraDetails7 = () => Object.entries(cookieToSalesforceFieldMap).map(([cookieName, fieldName]) => ({
  label: fieldName,
  value: getCookie(cookieName),
  displayToAgent: true,
  transcriptFields: [fieldName]
})).filter((detail) => detail.value !== null);
var tracking_cookies_default = getInitialExtraDetails7;

// src/extra-details/url.js
var getInitialExtraDetails8 = () => [
  {
    label: "googlePageUrl",
    value: window.location.href,
    displayToAgent: true,
    transcriptFields: ["Google_Page_Url__c"]
  }
];
var url_default = getInitialExtraDetails8;

// src/extra-details/prechat-form.js
var getInitialPrechatFormDetails = () => [
  {
    label: "Route To",
    value: "",
    transcriptFields: ["RouteTo__c"]
  },
  {
    label: "Account Id",
    value: "",
    transcriptFields: ["AccountId"]
  },
  {
    label: "Contact Id",
    value: "",
    transcriptFields: ["ContactId"]
  },
  {
    label: "Name",
    value: "",
    transcriptFields: ["Visitor_Name__c"]
  },
  {
    label: "Email",
    value: "",
    transcriptFields: ["Visitor_s_Email__c"]
  },
  {
    label: "How Help",
    value: "",
    transcriptFields: ["How_Can_We_Help__c"]
  },
  {
    label: "Company",
    value: "",
    transcriptFields: ["Company__c"]
  },
  {
    label: "Subject",
    value: "",
    transcriptFields: ["Subject__c"]
  },
  {
    label: "Department",
    value: "",
    transcriptFields: ["Requested_Department__c"]
  },
  {
    label: "PII Restricted Country",
    value: "",
    transcriptFields: ["PII_Restricted_Country__c"]
  },
  {
    label: "PII Data Processing",
    value: "",
    transcriptFields: ["PII_Data_Processing__c"]
  },
  {
    label: "PII Contact",
    value: "",
    transcriptFields: ["PII_Marketing_Contact__c"]
  },
  {
    label: "PII Detected Country",
    value: "",
    transcriptFields: ["PII_Detected_Country__c"]
  },
  {
    label: "Authenticated",
    value: "",
    transcriptFields: ["Authentication_Status__c"]
  },
  {
    label: "Type",
    value: "",
    transcriptFields: ["Type__c"]
  },
  {
    label: "SubType",
    value: "",
    transcriptFields: ["SubType__c"]
  },
  {
    label: "SupportPIN",
    value: "",
    transcriptFields: ["Visitor_PIN__c"]
  },
  {
    label: "AccountNumber",
    value: "",
    transcriptFields: ["Visitor_Account_Number__c"]
  },
  {
    label: "Business Name",
    value: "",
    transcriptFields: ["Business_Name__c"]
  },
  {
    label: "Business Email",
    value: "",
    transcriptFields: ["Business_Email__c"]
  },
  {
    label: "Title",
    value: "",
    transcriptFields: ["Title__c"]
  }
];
var prechat_form_default = getInitialPrechatFormDetails;

// src/extra-details.js
var getInitialAsyncDetails = () => Promise.all([google_analytics_default()]).then((results) => results.flat());
var toFieldsObject = (details) => Object.fromEntries(details.map(({ name, value }) => [name, value]));
var getInitialPrechatFields = () => getInitialAsyncDetails().then(async (asyncDetails) => {
  const hiddenPrechatFields = {
    ...toFieldsObject(asyncDetails),
    ...toFieldsObject(affiliate_default()),
    ...toFieldsObject(auth_token_default()),
    ...toFieldsObject(coupon_default()),
    ...toFieldsObject(pii_prefs_default()),
    ...toFieldsObject(preselect_department_default()),
    ...toFieldsObject(tracking_cookies_default()),
    ...toFieldsObject(url_default()),
    ...toFieldsObject(prechat_form_default())
  };
  return { hiddenPrechatFields };
});
var setPrechatFields = async ({ hiddenPrechatFields = {}, visiblePrechatFields = {} } = {}) => {
  const messaging = await getMessagingService();
  if (Object.keys(hiddenPrechatFields).length > 0) {
    await messaging.prechatAPI.setHiddenPrechatFields(hiddenPrechatFields);
  }
  if (Object.keys(visiblePrechatFields).length > 0) {
    await messaging.prechatAPI.setVisiblePrechatFields(visiblePrechatFields);
  }
};

// src/event-bridge.js
var eventMapping = {
  // Message Events
  onEmbeddedMessageSent: ["onAgentMessage", "onChasitorMessage", "onAgentRichMessage"],
  // Participant Events
  onEmbeddedMessagingConversationParticipantChanged: [
    "onAgentJoinedConference",
    "onAgentLeftConference"
  ],
  // Conversation Lifecycle Events
  onEmbeddedMessagingConversationStarted: ["onChatConferenceInitiated"],
  onEmbeddedMessagingConversationClosed: [
    "onChatConferenceEnded",
    "onChatEndedByAgent",
    "onChatEndedByChasitor",
    "afterDestroy"
  ],
  onEmbeddedMessagingConversationRouted: ["onChatTransferSuccessful", "onQueueUpdate"],
  // Window State Events
  onEmbeddedMessagingWindowMaximized: ["afterMaximize"],
  onEmbeddedMessagingWindowMinimized: ["afterMinimize"],
  // Form Events
  onEmbeddedMessagingPreChatSubmitted: ["onClickSubmitButton"],
  onEmbeddedMessagingButtonClicked: ["onHelpButtonClick"],
  // Initialization Events
  onEmbeddedMessagingReady: ["afterInit", "onSettingsCallCompleted"],
  // Connection Events
  onEmbeddedMessagingIdentityTokenExpired: ["onConnectionError"],
  onEmbeddedMessagingConversationOpened: ["onChatRequestSuccess"]
};
var mapAndEmitLegacyEvent = (newEvent, eventDetail) => {
  const legacyEvents = eventMapping[newEvent];
  if (!legacyEvents) return;
  if (newEvent === "onEmbeddedMessagingConversationParticipantChanged") {
    const isJoining = eventDetail?.payload?.participantJoined;
    const isAgent = eventDetail?.payload?.participant?.type === "AGENT";
    if (isAgent) {
      emitter_default.emit(isJoining ? "onAgentJoinedConference" : "onAgentLeftConference", eventDetail);
    }
    return;
  }
  if (newEvent === "onEmbeddedMessageSent") {
    const senderType = eventDetail?.payload?.message?.sender?.type;
    if (senderType === "AGENT") {
      const isRichMessage = eventDetail?.payload?.message?.messageContent?.type !== "Text";
      emitter_default.emit(isRichMessage ? "onAgentRichMessage" : "onAgentMessage", eventDetail);
    } else if (senderType === "END_USER") {
      emitter_default.emit("onChasitorMessage", eventDetail);
    }
    return;
  }
  if (newEvent === "onEmbeddedMessagingConversationRouted") {
    const routingResult = eventDetail?.payload?.routingResult;
    if (routingResult?.queueInfo) {
      emitter_default.emit("onQueueUpdate", {
        position: routingResult.queueInfo.position,
        ...routingResult.queueInfo
      });
    }
    if (routingResult?.routingSuccessful) {
      emitter_default.emit("onChatTransferSuccessful", eventDetail);
    }
    return;
  }
  if (newEvent === "onEmbeddedMessagingConversationClosed") {
    const closedBy = eventDetail?.payload?.closedBy?.type;
    if (closedBy === "AGENT") {
      emitter_default.emit("onChatEndedByAgent", eventDetail);
    } else if (closedBy === "END_USER") {
      emitter_default.emit("onChatEndedByChasitor", eventDetail);
    }
    emitter_default.emit("onChatConferenceEnded", eventDetail);
    emitter_default.emit("afterDestroy", eventDetail);
    return;
  }
  if (newEvent === "onEmbeddedMessagingConversationStarted") {
    emitter_default.emit("onChatConferenceInitiated", eventDetail);
    return;
  }
  if (newEvent === "onEmbeddedMessagingReady") {
    emitter_default.emit("afterInit", eventDetail);
    emitter_default.emit("onSettingsCallCompleted", eventDetail);
    return;
  }
  if (newEvent === "onEmbeddedMessagingConversationOpened") {
    emitter_default.emit("onChatRequestSuccess", eventDetail);
    return;
  }
  legacyEvents.forEach((legacyEvent) => {
    emitter_default.emit(legacyEvent, eventDetail);
  });
};
var setupEventListeners = () => {
  Object.keys(eventMapping).forEach((newEvent) => {
    window.addEventListener(newEvent, (event) => {
      mapAndEmitLegacyEvent(newEvent, event.detail);
    });
  });
  window.addEventListener("message", (event) => {
    const { eventName, detail } = event.data || {};
    if (eventName === "LWPrechatSurveySubmitted" && detail) {
      const prechatData = [
        {
          label: "Department",
          value: detail.department || ""
        },
        {
          label: "Email",
          value: detail.email || ""
        },
        {
          label: "Firstname",
          value: detail.firstname || ""
        },
        {
          label: "Lastname",
          value: detail.lastname || ""
        }
      ];
      emitter_default.emit("LWPrechatSurveySubmitted", prechatData);
      return;
    }
  });
};

// src/global-helpers.js
var globalHelpersObjectName = "lwChatEmbeddedSvcHelpers";
var globalHelpers = {
  config: {
    getConfig,
    getAllConfig
  },
  // This now uses our analytics.js to implement analytics more
  // generically, but we're preserving the googleAnalytics name
  // for compatibility in existing apps. Its API is the same.
  googleAnalytics: {
    sendEvent: sendEvent2
  }
};
window[globalHelpersObjectName] = globalHelpers;

// src/chat.js
var initPromise = null;
var initMessaging = async (messaging) => {
  const config = getAllConfig();
  const { orgId, deploymentId, communityURL, scrt2URL, settings } = config;
  const messagingSettings = {
    ...messaging.settings,
    ...settings
  };
  messaging.settings = messagingSettings;
  return messaging.init(orgId, deploymentId, communityURL, {
    scrt2URL
  });
};
var init2 = async (config) => {
  if (initPromise) return initPromise;
  if (config) mergeConfig(config);
  init();
  await loadMessagingService();
  setupEventListeners();
  const messaging = await getMessagingService();
  initPromise = initMessaging(messaging);
  return initPromise;
};
var awaitLaunchChatReady = (messaging, timeoutMs = 1e4) => new Promise((resolve, reject) => {
  if (messaging?.utilAPI?.launchChat) {
    resolve();
    return;
  }
  let timeoutId;
  const handleReady = (eventDetail) => {
    clearTimeout(timeoutId);
    window.removeEventListener("onEmbeddedMessagingReady", handleReady);
    resolve(eventDetail);
  };
  timeoutId = setTimeout(() => {
    window.removeEventListener("onEmbeddedMessagingReady", handleReady);
    emitter_default.emit("LWButtonClickTimeout");
    reject(new Error("Chat service not ready after 10 seconds"));
  }, timeoutMs);
  window.addEventListener("onEmbeddedMessagingReady", handleReady);
});
var setLastStartTimeCookie = () => {
  const { lastStartTimeCookieName, storageDomain } = getAllConfig();
  setCookie(lastStartTimeCookieName, (/* @__PURE__ */ new Date()).getTime(), {
    domain: storageDomain,
    expires: 365
  });
};
var launchChat = async ({ hiddenPrechatFields = {}, visiblePrechatFields = {} } = {}) => {
  if (!initPromise) throw new Error("Chat has not yet been initialized");
  const chatStartTimeout = getConfig("chatStartTimeout");
  const messaging = await getMessagingService();
  await awaitLaunchChatReady(messaging, chatStartTimeout);
  setLastStartTimeCookie();
  const { hiddenPrechatFields: initialHiddenFields } = await getInitialPrechatFields();
  const mergedHiddenFields = {
    ...initialHiddenFields,
    ...hiddenPrechatFields
  };
  if (Object.keys(mergedHiddenFields).length > 0 || Object.keys(visiblePrechatFields).length > 0) {
    await setPrechatFields({
      hiddenPrechatFields: mergedHiddenFields,
      visiblePrechatFields
    });
  }
  return messaging.utilAPI?.launchChat();
};
var startChat = launchChat;
export {
  emitter_default as emitter,
  getMessagingService,
  init2 as init,
  launchChat,
  startChat
};
